import { useState, useEffect, useMemo, useCallback } from "react"
import { format } from "date-fns"
import { DEFAULT_FREQUENCY, FREQUENCY_OPTIONS, type Frequency } from "./frequency"

export const STORAGE_KEY_FREQUENCY = "reporting_download_frequency"
export const STORAGE_KEY_START = "reporting_download_start"
export const STORAGE_KEY_STOP = "reporting_download_stop"

/** Get today's date in local YYYY-MM-DD format */
export function getTodayString(): string {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

/** Format a YYYY-MM-DD string into human-readable d MMM yyyy (e.g. 25 Aug 2026) */
export function formatDateLabel(isoDate: string): string {
  if (!isoDate) return ""
  try {
    const [year, month, day] = isoDate.split("-").map(Number)
    if (!year || !month || !day) return ""
    const date = new Date(year, month - 1, day)
    return format(date, "d MMM yyyy")
  } catch {
    return ""
  }
}

/** Read a query parameter from window.location.search if available. */
function getUrlParam(key: string): string | null {
  try {
    if (typeof window !== "undefined" && window.location?.search) {
      const params = new URLSearchParams(window.location.search)
      return params.get(key)
    }
  } catch {
    // Ignore non-browser environments
  }
  return null
}

/**
 * Determine the initial frequency, checking URL search params first,
 * then localStorage, falling back to DEFAULT_FREQUENCY.
 */
export function getInitialFrequency(): Frequency {
  const urlParam = getUrlParam("frequency")
  if (urlParam && FREQUENCY_OPTIONS.some((opt) => opt.value === urlParam)) {
    return urlParam as Frequency
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FREQUENCY)
    if (stored && FREQUENCY_OPTIONS.some((opt) => opt.value === stored)) {
      return stored as Frequency
    }
  } catch {
    // Ignore storage errors
  }
  return DEFAULT_FREQUENCY
}

/**
 * Determine the initial date range, checking URL params, localStorage, and falling back
 * to the default 30-day window clamped within effective bounds.
 */
export function getInitialDateRange(
  effectiveMinDate?: string,
  effectiveMaxDate?: string,
): { start: string; stop: string } {
  const today = getTodayString()
  const fallbackMax = effectiveMaxDate ?? today
  const defaultEndDate = new Date(fallbackMax)
  defaultEndDate.setDate(defaultEndDate.getDate() - 30)
  const defaultStart = defaultEndDate.toISOString().split("T")[0]
  const fallbackRange = {
    start: effectiveMinDate && defaultStart < effectiveMinDate ? effectiveMinDate : defaultStart,
    stop: fallbackMax,
  }

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  const urlStart = getUrlParam("start")
  const urlStop = getUrlParam("stop")

  let candStart = urlStart && dateRegex.test(urlStart) ? urlStart : null
  let candStop = urlStop && dateRegex.test(urlStop) ? urlStop : null

  if (!candStart || !candStop) {
    try {
      const storedStart = localStorage.getItem(STORAGE_KEY_START)
      const storedStop = localStorage.getItem(STORAGE_KEY_STOP)
      if (storedStart && dateRegex.test(storedStart) && storedStop && dateRegex.test(storedStop)) {
        candStart = storedStart
        candStop = storedStop
      }
    } catch {
      // Ignore storage errors
    }
  }

  if (candStart && candStop) {
    if (candStart <= candStop && candStart <= today && candStop <= today) {
      if (effectiveMaxDate && candStart > effectiveMaxDate) {
        return fallbackRange
      }
      if (effectiveMinDate && candStop < effectiveMinDate) {
        return fallbackRange
      }

      const clampedStart =
        effectiveMinDate && candStart < effectiveMinDate ? effectiveMinDate : candStart
      const clampedStop =
        effectiveMaxDate && candStop > effectiveMaxDate ? effectiveMaxDate : candStop

      if (clampedStart <= clampedStop) {
        return { start: clampedStart, stop: clampedStop }
      }
    }
  }

  return fallbackRange
}

/**
 * Persist the user's applied date range and frequency to localStorage and update
 * window URL search params without triggering a full page reload.
 */
export function persistDownloadPreferences(
  start: string,
  stop: string,
  frequency: Frequency,
): void {
  try {
    localStorage.setItem(STORAGE_KEY_START, start)
    localStorage.setItem(STORAGE_KEY_STOP, stop)
    localStorage.setItem(STORAGE_KEY_FREQUENCY, frequency)
  } catch {
    // Ignore storage errors
  }

  try {
    if (typeof window !== "undefined" && window.location && window.history) {
      const url = new URL(window.location.href)
      url.searchParams.set("start", start)
      url.searchParams.set("stop", stop)
      url.searchParams.set("frequency", frequency)
      window.history.replaceState(null, "", url.toString())
    }
  } catch {
    // Ignore non-browser environments
  }
}

export interface UseDownloadPreferencesOptions {
  minDate?: string
  maxDate?: string
}

export interface UseDownloadPreferencesReturn {
  start: string
  stop: string
  frequency: Frequency
  draftStart: string
  draftStop: string
  draftFrequency: Frequency
  effectiveMinDate?: string
  effectiveMaxDate: string
  today: string
  hasChanges: boolean
  isValidRange: boolean
  handleStartChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleStopChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleFrequencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  handleApply: () => void
  handleApplyPreset: (presetMin: string, presetMax: string) => void
  handleKeyDown: (e: React.KeyboardEvent) => void
}

/**
 * Custom hook managing reporting range dates, frequency granularity, draft inputs,
 * boundary clamping against asset/system active life, and persistent state.
 */
export function useDownloadPreferences({
  minDate,
  maxDate,
}: UseDownloadPreferencesOptions): UseDownloadPreferencesReturn {
  const effectiveMinDate = minDate
  const effectiveMaxDate = maxDate ?? getTodayString()
  const today = useMemo(() => getTodayString(), [])

  // Active / applied selection (drives data fetching)
  const initialDates = useMemo(
    () => getInitialDateRange(effectiveMinDate, effectiveMaxDate),
    [effectiveMinDate, effectiveMaxDate],
  )
  const initialFrequency = useMemo(() => getInitialFrequency(), [])

  const [start, setStart] = useState<string>(initialDates.start)
  const [stop, setStop] = useState<string>(initialDates.stop)
  const [frequency, setFrequency] = useState<Frequency>(initialFrequency)

  // Draft inputs (edited in the UI without live query triggers until confirmed)
  const [draftStart, setDraftStart] = useState<string>(start)
  const [draftStop, setDraftStop] = useState<string>(stop)
  const [draftFrequency, setDraftFrequency] = useState<Frequency>(frequency)

  // Re-align range if entity bounds change (e.g. user navigated to an older historical asset)
  useEffect(() => {
    if (effectiveMaxDate && (start > effectiveMaxDate || stop > effectiveMaxDate)) {
      const dates = getInitialDateRange(effectiveMinDate, effectiveMaxDate)
      setStart(dates.start)
      setStop(dates.stop)
      setDraftStart(dates.start)
      setDraftStop(dates.stop)
    } else if (effectiveMinDate && (start < effectiveMinDate || stop < effectiveMinDate)) {
      const dates = getInitialDateRange(effectiveMinDate, effectiveMaxDate)
      setStart(dates.start)
      setStop(dates.stop)
      setDraftStart(dates.start)
      setDraftStop(dates.stop)
    }
  }, [effectiveMinDate, effectiveMaxDate])

  const hasChanges = useMemo(() => {
    return (
      draftStart !== start ||
      draftStop !== stop ||
      draftFrequency !== frequency
    )
  }, [draftStart, start, draftStop, stop, draftFrequency, frequency])

  const isValidRange = useMemo(() => {
    if (!draftStart || !draftStop) return false
    if (draftStart > draftStop) return false
    if (draftStart > today || draftStop > today) return false
    return true
  }, [draftStart, draftStop, today])

  const handleApply = useCallback(() => {
    if (!isValidRange || !hasChanges) return
    setStart(draftStart)
    setStop(draftStop)
    setFrequency(draftFrequency)
    persistDownloadPreferences(draftStart, draftStop, draftFrequency)
  }, [isValidRange, hasChanges, draftStart, draftStop, draftFrequency])

  const handleApplyPreset = useCallback((presetMin: string, presetMax: string) => {
    setDraftStart(presetMin)
    setDraftStop(presetMax)
    setStart(presetMin)
    setStop(presetMax)
    persistDownloadPreferences(presetMin, presetMax, draftFrequency)
  }, [draftFrequency])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValidRange && hasChanges) {
      handleApply()
    }
  }, [handleApply, isValidRange, hasChanges])

  const handleFrequencyChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDraftFrequency(e.target.value as Frequency)
  }, [])

  const handleStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = e.target.value
    if (newStart > today) return
    setDraftStart(newStart)
  }, [today])

  const handleStopChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newStop = e.target.value
    if (newStop > today) return
    setDraftStop(newStop)
  }, [today])

  return {
    start,
    stop,
    frequency,
    draftStart,
    draftStop,
    draftFrequency,
    effectiveMinDate,
    effectiveMaxDate,
    today,
    hasChanges,
    isValidRange,
    handleStartChange,
    handleStopChange,
    handleFrequencyChange,
    handleApply,
    handleApplyPreset,
    handleKeyDown,
  }
}
